import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { logger } from "../logging/logger";
import { type Job, type JobStatus, jobsApi } from "./jobsClient";
import { jobsViewedStore } from "./jobsViewedStore";
import { useCancelJob, useConfirmJob, useJobsList, useRerunJob } from "./useJobs";

const TERMINAL: JobStatus[] = ["completed", "failed", "cancelled", "expired"];

function sortByRecency(jobs: Job[]): Job[] {
    // PRD-001 §Job-history: a CSM coming back the next morning expects
    // "the one I submitted last night" at the top. The API's DDB query
    // sorts by sk = JOB#${uuid} (random); resort by submittedAt here.
    return [...jobs].sort((a, b) => {
        const ta = Date.parse(a.submittedAt);
        const tb = Date.parse(b.submittedAt);
        return tb - ta;
    });
}

/**
 * Job history. Lists the user's own jobs (the API Lambda enforces
 * ownership via the JWT's userId claim — there's no server-side filter
 * to add here). The badge high-water mark is bumped to "now" on first
 * mount so any terminal-since-last-viewed jobs become "viewed."
 */
export function JobHistoryPage() {
    const { jobs, isLoading, error } = useJobsList();
    const cancel = useCancelJob();
    const rerun = useRerunJob();
    const confirm = useConfirmJob();
    const sortedJobs = useMemo(() => sortByRecency(jobs), [jobs]);

    useEffect(() => {
        jobsViewedStore.markViewedNow();
    }, []);

    if (isLoading) {
        return (
            <div className="empty-state" data-testid="jobs-loading">
                <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                <span>Loading job history…</span>
            </div>
        );
    }
    if (error) {
        return (
            <div className="callout callout-error" data-testid="jobs-error">
                <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
                <span>Could not load jobs: {error.message}</span>
            </div>
        );
    }
    if (jobs.length === 0) {
        return (
            <div data-testid="jobs-empty">
                <div className="page-header">
                    <div>
                        <div className="page-title">Job history</div>
                        <div className="page-help">No exports yet.</div>
                    </div>
                </div>
                <Link to="/" className="btn btn-primary">
                    <i className="fa-solid fa-plus" aria-hidden="true" /> Start an export
                </Link>
            </div>
        );
    }

    return (
        <div className="job-history">
            <div className="page-header">
                <div className="page-title">Job history</div>
                <Link to="/" className="btn btn-secondary btn-sm" data-testid="new-export-link">
                    <i className="fa-solid fa-plus" aria-hidden="true" /> New export
                </Link>
            </div>
            <table className="tbl job-table" data-testid="jobs-table">
                <thead>
                    <tr>
                        <th>Job name</th>
                        <th>Checklist</th>
                        <th>Subjects</th>
                        <th>Status</th>
                        <th title="Subjects whose evaluation finished successfully">Succeeded</th>
                        <th title="Subjects in the failures sidecar">Failed</th>
                        <th>Submitted</th>
                        <th>Auto-removal</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedJobs.map((job) => (
                        <JobRow
                            key={job.jobId}
                            job={job}
                            cancelDisabled={cancel.isPending}
                            rerunDisabled={rerun.isPending}
                            confirmDisabled={confirm.isPending}
                            onCancel={() => cancel.mutate(job.jobId)}
                            onRerun={() => rerun.mutate(job.jobId)}
                            onConfirm={() => confirm.mutate(job.jobId)}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

interface JobRowProps {
    job: Job;
    cancelDisabled: boolean;
    rerunDisabled: boolean;
    confirmDisabled: boolean;
    onCancel: () => void;
    onRerun: () => void;
    onConfirm: () => void;
}

function JobRow({
    job,
    cancelDisabled,
    rerunDisabled,
    confirmDisabled,
    onCancel,
    onRerun,
    onConfirm,
}: JobRowProps) {
    // Rev 13: cancel extends to gathering + awaiting-confirmation jobs
    // (PRD §Cancel — discards gathered IDs, no CSV artifacts).
    const isCancellable = ["queued", "gathering", "awaiting_confirmation", "running"].includes(job.status);
    const isAwaitingConfirmation = job.status === "awaiting_confirmation";
    const isTerminal = TERMINAL.includes(job.status);
    const hasResults = !!job.resultS3Key;
    // Rev 14: attribute-driven, never probe-driven — pre-rev-14 jobs have no
    // JSON artifact and must not show the button (PRD §Results affordance).
    const hasJsonResults = !!job.resultsJsonS3Key;
    // PRD-001 §Job-history: Failures button is only shown when the job
    // had at least one failed subject. The finalize Lambda still writes
    // a failures.csv sidecar (with header row only) for terminal jobs
    // with zero failures, but exposing it via the UI is pointless and
    // confusing — a CSM who sees a "Failures" button infers there were
    // failures.
    const hasFailures = !!job.failuresS3Key && (job.failedCount ?? 0) > 0;

    // PRD-001 §Job-history: succeeded + failed counts are shown for any
    // job past the queued state — running jobs (live counts annotated
    // by the API from the ResultsTable) and terminal-non-expired jobs
    // (authoritative finalize-written counts). Queued + expired jobs
    // show "—" because the counts aren't meaningful for them.
    const showCounts = job.status === "running" || (isTerminal && job.status !== "expired");

    return (
        <tr data-testid={`job-row-${job.jobId}`}>
            <td>
                {job.jobName}
                <FilterSummary job={job} />
            </td>
            <td className="td-dim">{job.checklistName ?? job.checklistId}</td>
            <td data-testid={`subjects-${job.jobId}`}>
                <SubjectsCell job={job} />
            </td>
            <td>
                <StatusBadge status={job.status} job={job} />
            </td>
            <td data-testid={`succeeded-count-${job.jobId}`}>
                {showCounts && typeof job.succeededCount === "number"
                    ? job.succeededCount
                    : "—"}
            </td>
            <td data-testid={`failed-count-${job.jobId}`}>
                {showCounts && typeof job.failedCount === "number"
                    ? job.failedCount
                    : "—"}
            </td>
            <td className="td-dim">{formatTimestamp(job.submittedAt)}</td>
            <td className="td-dim">
                {job.resultExpiresAt ? formatTimestamp(job.resultExpiresAt) : "—"}
            </td>
            <td className="actions">
                {hasResults && (
                    <DownloadButton
                        kind="results"
                        jobId={job.jobId}
                        label="Results (CSV)"
                    />
                )}
                {hasJsonResults && (
                    <DownloadButton
                        kind="result-json"
                        jobId={job.jobId}
                        label="Results (JSON)"
                        title="Raw per-subject payloads (NDJSON) — for programmatic use"
                    />
                )}
                {hasFailures && (
                    <DownloadButton
                        kind="failures"
                        jobId={job.jobId}
                        label="Failures"
                    />
                )}
                {isAwaitingConfirmation && (
                    <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={confirmDisabled}
                        onClick={onConfirm}
                        data-testid={`confirm-${job.jobId}`}
                    >
                        <i className="fa-solid fa-play" aria-hidden="true" /> Continue
                    </button>
                )}
                {isCancellable && (
                    <button
                        type="button"
                        disabled={cancelDisabled}
                        onClick={onCancel}
                        data-testid={`cancel-${job.jobId}`}
                    >
                        <i className="fa-solid fa-ban" aria-hidden="true" /> Cancel
                    </button>
                )}
                {isTerminal && (
                    <button
                        type="button"
                        disabled={rerunDisabled}
                        onClick={onRerun}
                        data-testid={`rerun-${job.jobId}`}
                    >
                        <i className="fa-solid fa-rotate-right" aria-hidden="true" /> Re-run
                    </button>
                )}
            </td>
        </tr>
    );
}

/**
 * Rev 13 (DoD §Filter-mode submit contract): the persisted filter parameters
 * display on the job entry — a dim sub-line under the job name. User-authored
 * search terms only (label verbatim, resolved UTC instant); never subject data.
 */
function FilterSummary({ job }: { job: Job }) {
    if (!job.filterCreatedStart && !job.filterLabel) return null;
    const parts: string[] = [];
    if (job.filterLabel) parts.push(`label: ${job.filterLabel}`);
    if (job.filterCreatedStart) parts.push(`created ≥ ${job.filterCreatedStart}`);
    return (
        <div
            className="td-dim"
            style={{ fontSize: 11, marginTop: 2 }}
            data-testid={`filter-summary-${job.jobId}`}
        >
            <i className="fa-solid fa-filter" aria-hidden="true" style={{ marginRight: 4 }} />
            {parts.join(" · ")}
        </div>
    );
}

/**
 * Subjects column. Rev 13: a filter-mode job's count is unknown until
 * gathering completes — show the matched-so-far count while gathering
 * (labelled "matched" to distinguish from evaluated — oracle panel input),
 * and "—" for a filter job that hasn't started. The count is the ONLY
 * subject data a filter-mode user ever sees (PRD §Filter privacy).
 */
function SubjectsCell({ job }: { job: Job }) {
    const known = job.subjectCount ?? job.subjectIds?.length;
    if (typeof known === "number") return <>{known}</>;
    if (job.status === "gathering") {
        return (
            <span data-testid={`gathering-count-${job.jobId}`}>
                {job.gatheredCount ?? 0} matched…
            </span>
        );
    }
    if (job.status === "awaiting_confirmation") {
        return <span data-testid={`matched-count-${job.jobId}`}>{job.gatheredCount ?? "—"} matched</span>;
    }
    return <>—</>;
}

function StatusBadge({ status, job }: { status: JobStatus; job: Job }) {
    // PRD-001 §Job-history: jobs in `running` state surface a live
    // progress percentage next to the status pill, derived from the
    // succeeded + failed counts the API annotates live from the
    // ResultsTable. Other statuses don't show a percentage — queued
    // has no work done, terminal statuses already surface the final
    // succeeded/failed counts in their own columns.
    const showProgress = status === "running";
    // Prefer the scalar subjectCount populated by the storage rework
    // (`prd-001-architecture-subjects-storage.md`); fall back to the
    // legacy inline subjectIds for pre-rework rows. Issue #11.
    const total = job.subjectCount ?? job.subjectIds?.length ?? 0;
    const processed = (job.succeededCount ?? 0) + (job.failedCount ?? 0);
    const pct = showProgress && total > 0
        ? Math.min(100, Math.round((processed / total) * 100))
        : null;
    // The worker flips `status` to terminal before the downstream
    // finalize Lambda writes `succeededCount` + the result-file keys.
    // Surface that intermediate state so a row mid-finalize doesn't
    // look broken (counts will populate, download buttons will appear
    // — useJobs.ts keeps polling until succeededCount lands).
    // Rev 13: jobs that terminated during gathering/awaiting carry
    // `terminalPhase` and never finalize — they are not "finalizing".
    const isFinalizing =
        ["completed", "failed", "cancelled"].includes(status) &&
        !job.terminalPhase &&
        typeof job.succeededCount !== "number";
    return (
        <span
            className={`status status-${status}`}
            data-testid={`status-${status}`}
        >
            {status === "awaiting_confirmation" ? "awaiting confirmation" : status}
            {pct !== null && (
                <span
                    className="status-progress"
                    data-testid={`progress-${job.jobId}`}
                    style={{ marginLeft: 6, opacity: 0.85 }}
                >
                    {pct}%
                </span>
            )}
            {isFinalizing && (
                <span
                    className="status-finalizing"
                    data-testid={`finalizing-${job.jobId}`}
                    style={{ marginLeft: 6, opacity: 0.7, fontStyle: "italic" }}
                >
                    finalizing…
                </span>
            )}
        </span>
    );
}

interface DownloadButtonProps {
    kind: "results" | "result-json" | "failures";
    jobId: string;
    label: string;
    title?: string;
}

// Explicit map — a two-branch ternary would silently route a new kind to the
// failures endpoint (rev-14 panel, general-purpose).
const DOWNLOAD_FETCHERS = {
    results: jobsApi.resultUrl,
    "result-json": jobsApi.resultJsonUrl,
    failures: jobsApi.failuresUrl,
} as const;

function DownloadButton({ kind, jobId, label, title }: DownloadButtonProps) {
    const [isFetching, setIsFetching] = useState(false);

    const handleClick = async () => {
        if (isFetching) return;
        setIsFetching(true);
        try {
            const { url } = await DOWNLOAD_FETCHERS[kind](jobId);
            window.open(url, "_blank", "noopener,noreferrer");
        } catch (err) {
            logger.error("download-url fetch failed", err);
        } finally {
            setIsFetching(false);
        }
    };

    return (
        <button
            type="button"
            onClick={handleClick}
            disabled={isFetching}
            title={title}
            data-testid={`download-${kind}-${jobId}`}
        >
            {isFetching ? (
                <>
                    <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" /> …
                </>
            ) : (
                <>
                    <i className="fa-solid fa-download" aria-hidden="true" /> {label}
                </>
            )}
        </button>
    );
}

function formatTimestamp(iso: string): string {
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
}
